import { useCallback, useMemo } from 'react';
import { AdviseEngagement, NewCurrentSupplier } from '../../../types/advise';
import {
  createAdviseCurrentSupplier,
  deleteAdviseCurrentSupplier,
  deleteAdviseSupplierInvoice,
  updateAdviseCurrentSuppliersSkip,
  uploadAdviseSupplierInvoice
} from '../../../Api/Advise/Engagements';
import { refusalMessage } from '../../../utils/refusalMessage';
import { AddedSupplier, EngagementCurrentSuppliers } from '../../Compounds/Advise/SideBySideView';
import { EngagementCurrentSpend } from '../../Compounds/Advise/SideBySideView/EngagementEditorContext';
import { addedSupplierId } from '../../Compounds/Advise/SideBySideView/AddCurrentSupplierModal.calc';
import { usePendingSaves } from './usePendingSaves';
import {
  CHANGE_FAILED,
  findSupplier,
  invoiceAdded,
  NO_SUPPLIERS,
  statusOf,
  uploadPauseMs
} from './currentSupplierRereads';
import { useCurrentSuppliersQueue } from './useCurrentSuppliersQueue';
import { useInvoiceViewSaves } from './useInvoiceViewSaves';
import { useManualSpendSaves } from './useManualSpendSaves';
import { useCurrentSpendOverride } from './useCurrentSpendOverride';

type AwaitOnFlush = ReturnType<typeof usePendingSaves>['awaitOnFlush'];

const SKIP_FAILED = "Couldn't save the $0 baseline. Please try again.";
const UPLOAD_FAILED = "Couldn't upload the invoice. Please try again.";
const ADD_FAILED = "Couldn't add the supplier. Please try again.";
const NOT_ADDED: AddedSupplier = { supplierId: null, refusal: null, refusedAsSent: false };

/** Saves the Current Suppliers step's changes one at a time; a refused skip is put back rather than sent again. */
export const useEngagementCurrentSuppliers = (
  engagement: AdviseEngagement | null,
  canEdit: boolean,
  awaitOnFlush: AwaitOnFlush,
  showPatch: (patch: Partial<AdviseEngagement>) => unknown
): EngagementCurrentSuppliers & { currentSpend: EngagementCurrentSpend } => {
  const queue = useCurrentSuppliersQueue(engagement, canEdit, awaitOnFlush, showPatch);
  const { engagementRef, saved, latest, show, showRefusal, sendInTurn, saveThenShow, postThenShow } = queue;
  const invoiceView = useInvoiceViewSaves(queue);
  const manualSpend = useManualSpendSaves(queue);
  const currentSpend = useCurrentSpendOverride(queue);

  const skip = useCallback(
    async (skipped: boolean) => {
      const id = engagementRef.current?.id;
      if (!canEdit || !id) return;
      const shown = engagementRef.current?.current_suppliers ?? NO_SUPPLIERS;
      show({ ...shown, skipped });
      const request = ++latest.current;
      const { data, error, now } = await sendInTurn(id, () => updateAdviseCurrentSuppliersSkip(id, skipped));
      if (engagementRef.current?.id !== id) return;
      if (!error) saved.current = data?.current_suppliers ?? { ...shown, skipped };
      else if (now) saved.current = now;
      if (request !== latest.current) return;
      if (error) showRefusal(refusalMessage(error, SKIP_FAILED, SKIP_FAILED));
      show(saved.current);
    },
    [canEdit, engagementRef, latest, saved, sendInTurn, show, showRefusal]
  );

  const removeSupplier = useCallback(
    (supplierId: string) =>
      saveThenShow(
        (id) => deleteAdviseCurrentSupplier(id, supplierId),
        CHANGE_FAILED,
        (now) => !findSupplier(now, supplierId)
      ),
    [saveThenShow]
  );

  const removeInvoice = useCallback(
    (supplierId: string, invoiceId: string) =>
      saveThenShow(
        (id) => deleteAdviseSupplierInvoice(id, supplierId, invoiceId),
        CHANGE_FAILED,
        (now) => !findSupplier(now, supplierId)?.invoices.some((invoice) => invoice.id === invoiceId)
      ),
    [saveThenShow]
  );

  const uploadInvoice = useCallback(
    async (supplierId: string, file: File) => {
      const posted = await postThenShow(
        (id) => uploadAdviseSupplierInvoice(id, supplierId, file),
        invoiceAdded(supplierId),
        uploadPauseMs(file)
      );
      if (!posted || posted.landed) return null;
      const refusal = refusalMessage(posted.sent.error, UPLOAD_FAILED, UPLOAD_FAILED);
      showRefusal(refusal);
      return refusal;
    },
    [postThenShow, showRefusal]
  );

  const addSupplier = useCallback(
    async (supplier: NewCurrentSupplier, invoice: File | null): Promise<AddedSupplier> => {
      const posted = await postThenShow(
        (id) => createAdviseCurrentSupplier(id, supplier),
        // A teammate's same-named supplier added while ours went unanswered is taken for ours: a rare race, accepted.
        (before, now) => !!addedSupplierId(before, now, supplier.name)
      );
      if (!posted) return NOT_ADDED;
      if (!posted.landed) {
        const { error } = posted.sent;
        return {
          ...NOT_ADDED,
          refusal: refusalMessage(error, ADD_FAILED, ADD_FAILED),
          refusedAsSent: statusOf(error) === 422
        };
      }
      const supplierId = addedSupplierId(posted.before, saved.current, supplier.name);
      if (!supplierId) {
        // It saved, but under a name this can't find, so the invoice has nowhere to go but the card's own upload.
        if (invoice) showRefusal(UPLOAD_FAILED);
        return NOT_ADDED;
      }
      if (invoice) await uploadInvoice(supplierId, invoice);
      return { ...NOT_ADDED, supplierId };
    },
    [postThenShow, saved, uploadInvoice, showRefusal]
  );

  return useMemo(
    () => ({
      skip,
      removeSupplier,
      uploadInvoice,
      removeInvoice,
      addSupplier,
      currentSpend,
      ...invoiceView,
      ...manualSpend
    }),
    [skip, removeSupplier, uploadInvoice, removeInvoice, addSupplier, currentSpend, invoiceView, manualSpend]
  );
};
